export interface Collection {
  id: string;
  slug: string;
  name: string;
  subtitle: string;
  category: string;
  categorySlug: string;
  description: string;
  longDescription: string;
  features: string[];
  heroImage: string;
}

export const collectionsData: Collection[] = [
  // Hardwood Collections
  {
    id: "solo",
    slug: "solo-collection",
    name: "The Solo Collection",
    subtitle: "Real Wood",
    category: "Hardwood",
    categorySlug: "hardwood",
    description: "Premium solid and engineered hardwood flooring",
    longDescription: "Premium solid and engineered hardwood flooring. Timeless elegance that adds warmth and value to any home. Each plank is carefully selected for its unique grain patterns and natural beauty.",
    features: ["Real Wood", "25-Year Warranty", "Expert Installation Included"],
    heroImage: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1920&auto=format&fit=crop",
  },
  {
    id: "canto",
    slug: "canto-collection",
    name: "The Canto Collection",
    subtitle: "Custom Hardwood",
    category: "Hardwood",
    categorySlug: "hardwood",
    description: "Bespoke handcrafted hardwood floors",
    longDescription: "Bespoke handcrafted hardwood floors designed to your exact specifications. Custom stains, widths, and finishes available. The ultimate in personalized flooring luxury.",
    features: ["Custom Designs", "Hand-Scraped Options", "Exclusive Patterns"],
    heroImage: "https://images.unsplash.com/photo-1560185893-a55cbc8c57e8?w=1920&auto=format&fit=crop",
  },
  // Vinyl Collection
  {
    id: "evo",
    slug: "evo-collection",
    name: "The Evo Collection",
    subtitle: "Luxury Vinyl Plank",
    category: "Vinyl",
    categorySlug: "vinyl",
    description: "100% waterproof luxury vinyl plank flooring",
    longDescription: "100% waterproof luxury vinyl plank flooring. Perfect for kitchens, bathrooms, and basements. Realistic wood and stone looks with superior durability that stands up to life's toughest moments.",
    features: ["100% Waterproof", "Click-Lock Install", "Pet & Kid Friendly"],
    heroImage: "https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?w=1920&auto=format&fit=crop",
  },
  // Carpet Collections
  {
    id: "echo",
    slug: "echo-collection",
    name: "The Echo Collection",
    subtitle: "Pattern Carpet",
    category: "Carpet",
    categorySlug: "carpet",
    description: "Commercial-grade pattern carpet",
    longDescription: "Commercial-grade pattern carpet designed to hide dirt and wear. Ideal for high-traffic areas and busy households. Beautiful patterns that maintain their appearance year after year.",
    features: ["Pattern Hides Dirt", "Commercial Grade", "CRI Green Label Plus"],
    heroImage: "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=1920&auto=format&fit=crop",
  },
  {
    id: "verso",
    slug: "verso-collection",
    name: "The Verso Collection",
    subtitle: "Texture Carpet",
    category: "Carpet",
    categorySlug: "carpet",
    description: "Soft, plush texture carpet",
    longDescription: "Soft, plush texture carpet perfect for bedrooms and living spaces. Luxurious underfoot comfort with stain-resistant technology that keeps your floors looking fresh.",
    features: ["Soft Touch", "Stain Resistant", "Hypoallergenic"],
    heroImage: "https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=1920&auto=format&fit=crop",
  },
  // Laminate Collection
  {
    id: "novo",
    slug: "novo-collection",
    name: "The Novo Collection",
    subtitle: "Laminated Wood",
    category: "Laminate",
    categorySlug: "laminate",
    description: "Affordable and durable laminate flooring",
    longDescription: "Affordable and durable laminate flooring that delivers the look of real wood at a fraction of the cost. Easy DIY installation with click-lock technology for a seamless finish.",
    features: ["Budget Friendly", "Easy DIY Install", "Water Resistant"],
    heroImage: "https://images.unsplash.com/photo-1595428774223-ef52624120d2?w=1920&auto=format&fit=crop",
  },
  // Tile Collections
  {
    id: "flow",
    slug: "flow-collection",
    name: "The Flow Collection",
    subtitle: "Porcelain Tile",
    category: "Tile",
    categorySlug: "tile",
    description: "Classic and modern porcelain tile designs",
    longDescription: "Classic and modern porcelain tile designs for floors, walls, and backsplashes. Timeless beauty with exceptional durability that lasts a lifetime.",
    features: ["Scratch Proof", "Heat Resistant", "Low Maintenance"],
    heroImage: "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=1920&auto=format&fit=crop",
  },
  {
    id: "hydro",
    slug: "hydro-collection",
    name: "The Hydro Collection",
    subtitle: "Engineered Tile",
    category: "Tile",
    categorySlug: "tile",
    description: "Advanced engineered tile with waterproof technology",
    longDescription: "Advanced engineered tile with superior waterproof technology. Perfect for bathrooms, kitchens, and outdoor spaces. Luxury aesthetics with unmatched performance.",
    features: ["100% Waterproof", "Luxury Look", "Easy Maintenance"],
    heroImage: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1920&auto=format&fit=crop",
  },
];

// Get collections by category slug
export const getCollectionsByCategory = (categorySlug: string): Collection[] => {
  return collectionsData.filter((c) => c.categorySlug === categorySlug);
};

// Get a single collection by its slug
export const getCollectionBySlug = (slug: string): Collection | undefined => {
  return collectionsData.find((c) => c.slug === slug);
};

// Get collection by ID
export const getCollectionById = (id: string): Collection | undefined => {
  return collectionsData.find((c) => c.id === id);
};

// Category metadata
export const categoryMeta: Record<string, { title: string; description: string }> = {
  hardwood: {
    title: "Hardwood Flooring",
    description: "Premium solid and engineered hardwood flooring collections. Timeless elegance that adds warmth and value to any home.",
  },
  vinyl: {
    title: "Vinyl Flooring",
    description: "Waterproof luxury vinyl plank and tile collections. Durable, stylish, and perfect for any room.",
  },
  carpet: {
    title: "Carpet Flooring",
    description: "Soft, comfortable carpet collections from plush textures to durable patterns. Perfect for bedrooms and living spaces.",
  },
  laminate: {
    title: "Laminate Flooring",
    description: "Affordable and durable laminate flooring that delivers the look of real wood at a fraction of the cost.",
  },
  tile: {
    title: "Tile Flooring",
    description: "Ceramic and porcelain tile collections for floors, walls, and backsplashes. Water-resistant and easy to maintain.",
  },
};
